import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAuthSession, clearAuthSession } from '../services/api';

const DashboardSidebar = ({ userRole }) => {
  const navigate = useNavigate();
  const authSession = getAuthSession();
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const handleLogout = () => {
    clearAuthSession();
    navigate('/login');
  };

  const getNavLinks = () => {
    if (userRole === 'admin') {
      return [
        { icon: '📊', label: 'Dashboard', path: '/admin-dashboard' },
        { icon: '📑', label: 'Worksheets Library', path: '/admin/worksheets' },
        { icon: '📝', label: 'Worksheet Form Builder', path: '/admin/worksheet-forms' },
        { icon: '💡', label: 'Self-Help Tools', path: '/admin/self-help' },
        { icon: '✓', label: 'Verify Therapists Docs', path: '/admin/verifications' },
        { icon: '👨‍⚕️', label: 'Therapists', path: '/admin/therapists' },
        { icon: '🏷️', label: 'Therapist Categories', path: '/admin/therapist-categories' },
        { icon: '📚', label: 'Blog Categories', path: '/admin/blog-categories' },
        { icon: '🛋️', label: 'Session Types', path: '/admin/session-types' },
        { icon: '👥', label: 'Manage Users', path: '/admin/users' },
        { icon: '🎉', label: 'Events & Seminars', path: '/admin/events' },
        { icon: '🗓️', label: 'Event Publish Weeks', path: '/admin/event-publish-plans' },
        { icon: '📝', label: 'Blog & Content', path: '/admin/content' },
        { icon: '🚫', label: 'Prohibited Words', path: '/admin/prohibited-words' },
        { icon: '❓', label: 'Manage FAQs', path: '/admin/faqs' },
        { icon: 'ℹ️', label: 'About Us Content', path: '/admin/about-us' },
        { icon: '💬', label: "Founder's Message", path: '/admin/founders-message' },
        { icon: '🌟', label: 'Showcase Slider', path: '/admin/showcase' },
        { icon: '📞', label: 'Contact & Inquiries', path: '/admin/contact' },
        { icon: '📈', label: 'Analytics', path: '/admin/analytics' }
      ];
    } else if (userRole === 'physiotherapist') {
      return [
        { icon: '📊', label: 'Dashboard', path: '/physio-dashboard' },
        { icon: '💬', label: 'Community Posts', path: '/therapist/community' },
        { icon: '📑', label: 'Clinical Worksheets', path: '/therapist/worksheets' },
        { icon: '📅', label: 'My Bookings', path: '/therapist/bookings' },
        { icon: '⏰', label: 'Availability', path: '/therapist/availability' },
        { icon: '👤', label: 'My Profile', path: '/therapist/profile' },
        { icon: '✍️', label: 'Write Blog', path: '/therapist/blogs/new' },
        { icon: '🎉', label: 'Create Event', path: '/therapist/events/new' },
        { icon: '📚', label: 'Upload Resource', path: '/therapist/resources/upload' }
      ];
    } else {
      return [
        { icon: '📊', label: 'Dashboard', path: '/client-dashboard' },
        { icon: '👤', label: 'My Profile', path: '/client/profile' },
        { icon: '📑', label: 'My Worksheets', path: '/client/worksheets' },
        { icon: '🔍', label: 'Find Therapist', path: '/therapists' },
        { icon: '📅', label: 'My Bookings', path: '/client/bookings' },
        { icon: '💭', label: 'Self-Help', path: '/self-help' },
        { icon: '👥', label: 'Community', path: '/community' }
      ];
    }
  };

  const navLinks = getNavLinks();

  return (
    <>
      {/* Mobile Toggle Button */}
      <button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        style={{
          display: 'none',
          position: 'fixed',
          top: '20px',
          left: '20px',
          zIndex: '1000',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          padding: '10px 15px',
          cursor: 'pointer',
          fontSize: '18px'
        }}
        className="sidebar-mobile-toggle"
      >
        ☰
      </button>

      {/* Sidebar */}
      <aside
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          height: '100vh',
          width: '280px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '30px 0',
          overflowY: 'auto',
          zIndex: '999',
          boxShadow: '2px 0 10px rgba(0,0,0,0.1)'
        }}
      >
        {/* Brand/Logo */}
        <div style={{ padding: '0 20px', marginBottom: '40px', textAlign: 'center' }}>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>🧠 MentalHealth</h2>
          <p style={{ margin: '5px 0 0 0', fontSize: '12px', opacity: 0.9 }}>
            {userRole === 'admin' ? 'Admin Panel' : userRole === 'physiotherapist' ? 'Therapist Dashboard' : 'Client Dashboard'}
          </p>
        </div>

        {/* User Info */}
        <div style={{ padding: '0 20px', marginBottom: '30px', paddingBottom: '20px', borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
          <p style={{ margin: '0 0 5px 0', fontSize: '14px', opacity: 0.9 }}>Logged in as</p>
          <p style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>{authSession?.user?.name || 'User'}</p>
          <p style={{ margin: '5px 0 0 0', fontSize: '12px', opacity: 0.8 }}>{authSession?.user?.email}</p>
        </div>

        {/* Navigation Links */}
        <nav style={{ marginBottom: '40px' }}>
          {navLinks.map((link, idx) => (
            <Link
              key={idx}
              to={link.path}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '15px 20px',
                color: 'white',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: 500,
                transition: 'background 0.3s',
                borderLeft: '4px solid transparent'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                e.currentTarget.style.borderLeftColor = 'rgba(255,255,255,0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderLeftColor = 'transparent';
              }}
            >
              <span style={{ fontSize: '18px' }}>{link.icon}</span>
              <span>{link.label}</span>
            </Link>
          ))}
        </nav>

        {/* Bottom Actions */}
        <div style={{ padding: '0 20px', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '20px' }}>
          {/* Main Website Link */}
          <a
            href="/"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 15px',
              background: 'rgba(255,255,255,0.2)',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 500,
              marginBottom: '10px',
              textAlign: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'background 0.3s'
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.3)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')}
          >
            🏠 Go to Website
          </a>

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            style={{
              width: '100%',
              padding: '12px 15px',
              background: 'rgba(255,255,255,0.3)',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.5)',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'background 0.3s'
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.5)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.3)')}
          >
            🚪 Logout
          </button>
        </div>
      </aside>

      {/* Content Wrapper - Add left padding for sidebar */}
      <style>{`
        .sidebar-content-wrapper {
          margin-left: 280px;
          transition: margin-left 0.3s;
        }

        @media (max-width: 768px) {
          .sidebar-content-wrapper {
            margin-left: 0;
          }
          aside[style*="position: fixed"] {
            transform: translateX(-100%);
            transition: transform 0.3s;
          }
          aside[style*="position: fixed"].mobile-open {
            transform: translateX(0);
          }
          .sidebar-mobile-toggle {
            display: block !important;
          }
        }
      `}</style>
    </>
  );
};

export default DashboardSidebar;
